import { CombatantKind, Tank } from './scorched-earth.types';

/** Round wins dominate; credits and remaining HP break ties within a match. */
export const SCORE_PER_ROUND_WIN = 1000;
export const SCORE_PER_CREDIT = 1;
export const SCORE_PER_HEALTH = 1;
export const BEST_SCORES_KEY = 'scorched-earth-bests-v1';
export const BEST_SCORES_LIMIT = 10;

export interface MatchStanding {
	playerId: number;
	name: string;
	color: string;
	kind: CombatantKind;
	score: number;
	roundWins: number;
	credits: number;
	health: number;
	alive: boolean;
	rank: number;
}

export interface BestScoreEntry {
	name: string;
	score: number;
	roundWins: number;
	credits: number;
	recordedAt: number;
}

export interface BestScoresStore {
	version: 1;
	entries: BestScoreEntry[];
}

export type ScorePlayerStats = Pick<Tank, 'roundWins' | 'credits' | 'health' | 'alive' | 'name' | 'id' | 'color' | 'kind'>;

/**
 * Match score from existing combat stats:
 * score = roundWins×1000 + credits + (alive ? health : 0)
 */
export function computePlayerMatchScore(player: Pick<Tank, 'roundWins' | 'credits' | 'health' | 'alive'>): number {
	const wins = Math.max(0, Math.floor(Number(player.roundWins) || 0));
	const credits = Math.max(0, Math.floor(Number(player.credits) || 0));
	const health = player.alive ? Math.max(0, Math.floor(Number(player.health) || 0)) : 0;
	return wins * SCORE_PER_ROUND_WIN + credits * SCORE_PER_CREDIT + health * SCORE_PER_HEALTH;
}

export function compareMatchStandings(
	a: Pick<MatchStanding, 'score' | 'roundWins' | 'credits' | 'name'>,
	b: Pick<MatchStanding, 'score' | 'roundWins' | 'credits' | 'name'>,
): number {
	if (b.score !== a.score) {
		return b.score - a.score;
	}
	if (b.roundWins !== a.roundWins) {
		return b.roundWins - a.roundWins;
	}
	if (b.credits !== a.credits) {
		return b.credits - a.credits;
	}
	return String(a.name || '').localeCompare(String(b.name || ''));
}

export function buildMatchStandings(players: ScorePlayerStats[]): MatchStanding[] {
	const ranked = (players || [])
		.map((p) => ({
			playerId: p.id,
			name: p.name || `Player ${p.id + 1}`,
			color: p.color || '#94a3b8',
			kind: p.kind === 'cpu' ? ('cpu' as const) : ('human' as const),
			score: computePlayerMatchScore(p),
			roundWins: Math.max(0, Math.floor(Number(p.roundWins) || 0)),
			credits: Math.max(0, Math.floor(Number(p.credits) || 0)),
			health: Math.max(0, Math.floor(Number(p.health) || 0)),
			alive: !!p.alive,
			rank: 0,
		}))
		.sort(compareMatchStandings);
	return ranked.map((entry, index) => ({ ...entry, rank: index + 1 }));
}

export function emptyBestScoresStore(): BestScoresStore {
	return { version: 1, entries: [] };
}

/** Safe backward-compatible parse of persisted local bests. */
export function parseBestScores(raw: string | null | undefined): BestScoresStore {
	if (!raw || typeof raw !== 'string') {
		return emptyBestScoresStore();
	}
	try {
		const parsed = JSON.parse(raw) as Partial<BestScoresStore> & { entries?: unknown };
		if (!parsed || typeof parsed !== 'object') {
			return emptyBestScoresStore();
		}
		const version = Number((parsed as { version?: unknown }).version);
		if (version !== 1) {
			return emptyBestScoresStore();
		}
		const list = Array.isArray(parsed.entries) ? parsed.entries : [];
		const entries: BestScoreEntry[] = [];
		for (const item of list) {
			if (!item || typeof item !== 'object') {
				continue;
			}
			const row = item as Partial<BestScoreEntry>;
			const name = typeof row.name === 'string' ? row.name.trim() : '';
			const score = Math.floor(Number(row.score));
			if (!name || !Number.isFinite(score) || score < 0) {
				continue;
			}
			entries.push({
				name: name.slice(0, 32),
				score,
				roundWins: Math.max(0, Math.floor(Number(row.roundWins) || 0)),
				credits: Math.max(0, Math.floor(Number(row.credits) || 0)),
				recordedAt: Number.isFinite(Number(row.recordedAt)) ? Number(row.recordedAt) : 0,
			});
		}
		entries.sort((a, b) => b.score - a.score || b.recordedAt - a.recordedAt);
		return { version: 1, entries: entries.slice(0, BEST_SCORES_LIMIT) };
	} catch {
		return emptyBestScoresStore();
	}
}

export function serializeBestScores(store: BestScoresStore): string {
	const safe = parseBestScores(JSON.stringify(store));
	return JSON.stringify(safe);
}

/**
 * Merge new standings into the local hall of fame.
 * Keeps each name's personal best, then the global top N by score.
 */
export function mergeBestScores(
	existing: BestScoresStore,
	standings: MatchStanding[],
	recordedAt = Date.now(),
): BestScoresStore {
	const byName = new Map<string, BestScoreEntry>();
	for (const entry of parseBestScores(JSON.stringify(existing)).entries) {
		byName.set(entry.name.toLowerCase(), entry);
	}
	for (const standing of standings) {
		if (!standing?.name || standing.score <= 0) {
			continue;
		}
		const key = standing.name.toLowerCase();
		const prior = byName.get(key);
		if (!prior || standing.score > prior.score) {
			byName.set(key, {
				name: standing.name.slice(0, 32),
				score: standing.score,
				roundWins: standing.roundWins,
				credits: standing.credits,
				recordedAt,
			});
		}
	}
	const entries = Array.from(byName.values()).sort((a, b) => b.score - a.score || b.recordedAt - a.recordedAt);
	return { version: 1, entries: entries.slice(0, BEST_SCORES_LIMIT) };
}

export function loadBestScoresFromStorage(storage: Storage | null | undefined): BestScoresStore {
	if (!storage) {
		return emptyBestScoresStore();
	}
	try {
		return parseBestScores(storage.getItem(BEST_SCORES_KEY));
	} catch {
		return emptyBestScoresStore();
	}
}

export function saveBestScoresToStorage(storage: Storage | null | undefined, store: BestScoresStore): BestScoresStore {
	const normalized = parseBestScores(JSON.stringify(store));
	if (!storage) {
		return normalized;
	}
	try {
		storage.setItem(BEST_SCORES_KEY, serializeBestScores(normalized));
	} catch {
		// Ignore quota / private-mode failures.
	}
	return normalized;
}

export function personalBestForName(store: BestScoresStore, name: string): BestScoreEntry | null {
	const key = (name || '').trim().toLowerCase();
	if (!key) {
		return null;
	}
	return store.entries.find((e) => e.name.toLowerCase() === key) ?? null;
}
